import { DataTablePagination } from 'components/common'
import SearchBar from 'components/common/dataTable/searchBar'
import { Table, TableCell, TableRow } from 'components/common/minimalTable'
import { usePageAccess, useQuery } from 'hooks'
import { ChangeEvent, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { SyncedPickersData } from 'types/pickerMap'
import { getPickersApi } from 'utils/api/pickerEmpMapApi'
import { formatDate } from 'utils/helpers'
import PickerFullSyncButton from './pickerFullSyncButton'

const SELECT_CLASS =
  'h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900'

// Pickers synced from Order Tracker - view only, filters kept in the URL
const PickerList = () => {
  const [data, setData] = useState<SyncedPickersData>({ docs: [] })
  const [isLoading, setIsLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)
  const { isEditor } = usePageAccess()

  const { search } = useLocation()
  const { getAllQueries, getQuery, setMultipleQueries } = useQuery()

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      try {
        const {
          page,
          limit,
          search: text,
          role,
          isActive,
          store,
        } = getAllQueries()
        const { data: pickers } = await getPickersApi({
          page,
          limit,
          search: text,
          role,
          isActive,
          store,
        })
        setData(pickers)
      } catch {
        setData({ docs: [] })
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [search, getAllQueries, reloadKey])

  // Changing a filter goes back to page 1
  const setFilter =
    (key: string) =>
    ({ target }: ChangeEvent<HTMLSelectElement | HTMLInputElement>) =>
      setMultipleQueries({ [key]: target.value || undefined, page: 1 })

  return (
    <div className="mt-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="title">Pickers</h3>
          <p className="mb-3 text-xs text-gray-500">
            View only - synced from Order Tracker.
          </p>
        </div>
        {isEditor && (
          <PickerFullSyncButton
            onSynced={() => setReloadKey((key) => key + 1)}
          />
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="min-w-[240px] flex-1">
          <SearchBar placeholder="Search name, picker ID, emp ID, email" />
        </div>
        <select
          className={SELECT_CLASS}
          value={getQuery('role') ?? ''}
          onChange={setFilter('role')}
        >
          <option value="">All roles</option>
          <option value="PICKER">Picker</option>
          <option value="SUPERVISOR">Supervisor</option>
        </select>
        <select
          className={SELECT_CLASS}
          value={String(getQuery('isActive') ?? '')}
          onChange={setFilter('isActive')}
        >
          <option value="">All statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <input
          className={SELECT_CLASS}
          placeholder="Store code"
          defaultValue={getQuery('store') ?? ''}
          onBlur={setFilter('store')}
        />
      </div>

      <Table
        headers={[
          'Picker ID',
          'Name',
          'Emp ID',
          'Email',
          'Role',
          'Status',
          'PIN',
          'Old App Store',
          'Picker Names',
          'Last Synced',
        ]}
      >
        {isLoading ? (
          <TableRow>
            <TableCell>Loading...</TableCell>
          </TableRow>
        ) : data.docs.length ? (
          data.docs.map((picker) => (
            <TableRow key={picker.pickerId}>
              <TableCell>{picker.pickerId}</TableCell>
              <TableCell>
                {`${picker.firstName ?? ''} ${picker.lastName ?? ''}`.trim() ||
                  '-'}
              </TableCell>
              <TableCell>{picker.empId || '-'}</TableCell>
              <TableCell>{picker.email || '-'}</TableCell>
              <TableCell>{picker.role || '-'}</TableCell>
              <TableCell>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    picker.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {picker.isActive ? 'Active' : 'Inactive'}
                </span>
              </TableCell>
              <TableCell>{picker.pinId || '-'}</TableCell>
              <TableCell>{picker.legacyStore || '-'}</TableCell>
              <TableCell>
                {picker.preparedByNames?.length
                  ? picker.preparedByNames.join(', ')
                  : '-'}
              </TableCell>
              <TableCell>
                {picker.syncedAt
                  ? formatDate(picker.syncedAt, { includeTime: true })
                  : '-'}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell>No pickers found</TableCell>
          </TableRow>
        )}
      </Table>

      <DataTablePagination
        parentClassName="px-4 border-t border-gray-200 py-3 my-0"
        currentPage={data?.page}
        totalPages={data?.totalPages}
        hasNextPage={data?.hasNextPage}
        hasPrevPage={data?.hasPrevPage}
        limit={data?.limit}
        nextPage={data?.nextPage}
        prevPage={data?.prevPage}
        disabled={isLoading}
      />
    </div>
  )
}

export default PickerList
